import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, ArrowRight, Eye, BookOpen, PenTool, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePopupContext } from '../contexts/PopupContext';
import { useSectionAnimationContext } from '../contexts/SectionAnimationContext';
import { blogs } from '../data/blogs';
import LiquidBlobs from './LiquidBlobs';

const BlogSection = () => {
  const [filter, setFilter] = useState('all');
  const [hoveredPost, setHoveredPost] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const { showSuccess, showError } = usePopupContext();
  const { getSectionClasses } = useSectionAnimationContext();

  // Use static data instead of API call
  const blogsData = blogs;

  // Add scroll functionality for blog cards
  const blogScrollContainer = React.useRef(null);
  const blogScrollContainer2 = React.useRef(null);

  const scrollBlogLeft = () => {
    if (blogScrollContainer.current) {
      blogScrollContainer.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollBlogRight = () => {
    if (blogScrollContainer.current) {
      blogScrollContainer.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const scrollBlogLeft2 = () => {
    if (blogScrollContainer2.current) {
      blogScrollContainer2.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollBlogRight2 = () => {
    if (blogScrollContainer2.current) {
      blogScrollContainer2.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const categories = ['all', 'AI & Technology', 'Leadership', 'Investment Management', 'Process Improvement', 'Team Building'];
  
  const filteredPosts = blogsData.filter(post => 
    (filter === 'all' || post.category === filter) && post.status === 'published'
  ).sort((a, b) => new Date(b.updated_at || b.publishDate) - new Date(a.updated_at || a.publishDate)); // Sort by newest first

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleNewsletterSubscribe = (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      // Mock newsletter subscription
      showSuccess(
        `Thank you for subscribing with ${newsletterEmail}! You'll receive updates on AI, leadership, and innovation insights.`,
        'Newsletter Subscription Successful!'
      );
      setNewsletterEmail('');
    } else {
      showError('Please enter a valid email address.', 'Invalid Email');
    }
  };

  const handleReadMore = (blog) => {
    setSelectedBlog(blog);
    setShowBlogModal(true);
  };

  const handleCloseBlogModal = () => {
    setShowBlogModal(false);
    setSelectedBlog(null);
  };

  return (
    <section id="blog" className="py-24 bg-gradient-to-b from-gray-100 to-gray-50 relative overflow-hidden">
      <LiquidBlobs section="blog" />
      <div className="container mx-auto px-6 relative z-10">
        {/* Section content with genie animation */}
        <div className={getSectionClasses('blog')}>
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="heading-1 text-5xl md:text-7xl font-black text-text-primary mb-4 font-grotesk uppercase">
            Blog & <span className="text-indigo-600">Insights</span>
          </h2>
          <div className="w-24 h-1 bg-indigo-600 mx-auto mb-6"></div>
          <p className="body-medium text-text-secondary max-w-3xl mx-auto">
            Sharing insights on AI implementation, leadership strategies, and innovation in financial services. 
            Real experiences from building high-performance teams and driving digital transformation.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="flex items-center gap-2 mr-4">
            <Filter size={20} className="text-indigo-600" />
            <span className="text-text-secondary">Filter by:</span>
          </div>
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setFilter(category)}
              className={`btn-base ${
                filter === category 
                  ? 'btn-indigo' 
                  : 'btn-secondary'
              }`}
            >
              {category === 'all' ? 'All Posts' : category}
            </Button>
          ))}
        </div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-8">
              <Badge variant="outline" className="bg-indigo-600/10 text-indigo-600 border-indigo-600/30">
                Featured Article
              </Badge>
            </div>
            
            <Card
              className="group overflow-hidden glass-card hover:scale-[1.01] transition-all duration-500 cursor-pointer"
              onMouseEnter={() => setHoveredPost(filteredPosts[0].id)}
              onMouseLeave={() => setHoveredPost(null)}
              onClick={() => handleReadMore(filteredPosts[0])}
            >
              <div className="md:flex">
                {/* Featured Image */}
                <div className="md:w-1/2 relative overflow-hidden">
                  <img 
                    src={filteredPosts[0].image || '/api/placeholder/500/320'}
                    alt={filteredPosts[0].title}
                    className="w-full h-64 md:h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/500/320';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20"></div>
                </div>

                {/* Content */}
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <Badge className="bg-indigo-600 text-white font-medium">
                    {filteredPosts[0].category}
                  </Badge>
                  
                  <h3 className="heading-3 text-text-primary mb-4 group-hover:text-gradient transition-colors duration-300">
                    {filteredPosts[0].title}
                  </h3>
                  
                  <div 
                    className="body-medium text-text-secondary mb-6 rich-text-content"
                    dangerouslySetInnerHTML={{ __html: filteredPosts[0].excerpt }}
                  />
                  
                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-sm text-text-muted mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Clock size={16} className="mr-2" />
                        {filteredPosts[0].readTime}
                      </div>
                      <span>•</span>
                      <span>{formatDate(filteredPosts[0].updated_at || filteredPosts[0].publishDate)}</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={(e) => { e.stopPropagation(); handleReadMore(filteredPosts[0]); }}
                    className="btn-base btn-indigo"
                  >
                    Read Article
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Blog Two Independent Scrolling Rows */}
        <div className="space-y-8">
          {/* First Row - Always show first 4+ blog posts (excluding featured) */}
          <div className="relative">
            <Button
              onClick={scrollBlogLeft}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
              variant="ghost"
            >
              <ChevronLeft size={20} className="text-gray-700" />
            </Button>
            
            <Button
              onClick={scrollBlogRight}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
              variant="ghost"
            >
              <ChevronRight size={20} className="text-gray-700" />
            </Button>

            <div 
              ref={blogScrollContainer}
              className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {filteredPosts.slice(1, filteredPosts.length <= 5 ? filteredPosts.length : Math.max(5, Math.ceil((filteredPosts.length - 1) / 2) + 1)).map((post) => (
                <Card
                  key={post.id}
                  className={`group glass-card overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-[500px] flex-shrink-0 w-80 cursor-pointer ${
                    hoveredPost === post.id ? 'ring-2 ring-indigo-600/30' : ''
                  }`}
                  onMouseEnter={() => setHoveredPost(post.id)}
                  onMouseLeave={() => setHoveredPost(null)}
                  onClick={() => handleReadMore(post)}
                >
                  {/* Post Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image || '/api/placeholder/400/240'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/400/240';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent"></div>

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-indigo-600 text-white font-medium">
                        {post.category}
                      </Badge>
                    </div>
                  </div>

                  {/* Post Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    {/* Title */}
                    <h3 className="heading-5 text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <div
                      className="body-small text-text-secondary mb-4 line-clamp-3 rich-text-content flex-grow"
                      dangerouslySetInnerHTML={{ __html: post.excerpt }}
                    />

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags?.slice(0, 3)?.map((tag, index) => (
                        <Badge key={index} className="bg-gray-100 text-gray-700 px-2 py-1 text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {post.tags?.length > 3 && (
                        <Badge className="bg-gray-100 text-gray-600 px-2 py-1 text-xs">
                          +{post.tags.length - 3}
                        </Badge>
                      )}
                    </div>

                    {/* Meta Info & Read More */}
                    <div className="flex items-center justify-between text-sm text-text-muted pt-4 border-t border-gray-200 mt-auto">
                      <div className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {post.readTime}
                      </div>
                      <span className="inline-flex items-center text-indigo-600 font-semibold group-hover:translate-x-1 transition-transform duration-300">
                        Read More <ArrowRight size={14} className="ml-1" />
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Second Row - Only show if more than 4 blog posts (excluding featured) */}
          {filteredPosts.length > 5 && (
            <div className="relative">
              <Button
                onClick={scrollBlogLeft2}
                className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                variant="ghost"
              >
                <ChevronLeft size={20} className="text-gray-700" />
              </Button>
              
              <Button
                onClick={scrollBlogRight2}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
                variant="ghost"
              >
                <ChevronRight size={20} className="text-gray-700" />
              </Button>

              <div 
                ref={blogScrollContainer2}
                className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {filteredPosts.slice(Math.max(5, Math.ceil((filteredPosts.length - 1) / 2) + 1)).map((post) => (
                  <Card
                    key={post.id}
                    className={`group glass-card overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-[500px] flex-shrink-0 w-80 cursor-pointer ${
                      hoveredPost === post.id ? 'ring-2 ring-indigo-600/30' : ''
                    }`}
                    onMouseEnter={() => setHoveredPost(post.id)}
                    onMouseLeave={() => setHoveredPost(null)}
                    onClick={() => handleReadMore(post)}
                  >
                    {/* Post Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image || '/api/placeholder/400/240'}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/400/240';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent"></div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-indigo-600 text-white font-medium">
                          {post.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Title */}
                      <h3 className="heading-5 text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300 line-clamp-2">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <div
                        className="body-small text-text-secondary mb-4 line-clamp-3 rich-text-content flex-grow"
                        dangerouslySetInnerHTML={{ __html: post.excerpt }}
                      />

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags?.slice(0, 3)?.map((tag, index) => (
                          <Badge key={index} className="bg-gray-100 text-gray-700 px-2 py-1 text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {post.tags?.length > 3 && (
                          <Badge className="bg-gray-100 text-gray-600 px-2 py-1 text-xs">
                            +{post.tags.length - 3}
                          </Badge>
                        )}
                      </div>

                      {/* Meta Info & Read More */}
                      <div className="flex items-center justify-between text-sm text-text-muted pt-4 border-t border-gray-200 mt-auto">
                        <div className="flex items-center">
                          <Clock size={14} className="mr-1" />
                          {post.readTime}
                        </div>
                        <span className="inline-flex items-center text-indigo-600 font-semibold group-hover:translate-x-1 transition-transform duration-300">
                          Read More <ArrowRight size={14} className="ml-1" />
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* View All Blogs Button */}
        <div className="text-center mt-8">
          <Button 
            onClick={() => window.open('/blogs', '_blank')}
            className="btn-base btn-secondary btn-lg"
          >
            View All Blogs
            <ArrowRight size={20} className="ml-2" />
          </Button>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16">
          <Card className="glass-card p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <BookOpen size={48} className="mx-auto text-indigo-600 mb-4" />
              <h3 className="heading-3 text-text-primary mb-4">
                Stay Updated with Latest Insights
              </h3>
              <p className="body-medium text-text-secondary mb-6">
                Get notified when I publish new articles on AI, leadership, and innovation in finance. 
                Join fellow professionals who value actionable insights.
              </p>
              <form onSubmit={handleNewsletterSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <input 
                  type="email" 
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 glass-card border border-gray-200 rounded-full text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-indigo-600/50 backdrop-blur-sm"
                />
                <Button type="submit" className="btn-base btn-indigo">
                  <PenTool size={16} className="mr-2" />
                  Subscribe
                </Button>
              </form>
            </div>
          </Card>
        </div>
        {/* End genie animation wrapper */}
        </div>
      </div>

      {/* Blog Reading Modal - Large Immersive Experience */}
      {showBlogModal && selectedBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white w-full h-full max-w-8xl max-h-[95vh] overflow-hidden rounded-xl shadow-2xl m-4 flex flex-col">
            {/* Header Bar - Fixed */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center space-x-4">
                <Badge className="bg-indigo-600 text-white font-medium">
                  {selectedBlog.category}
                </Badge>
                <div className="flex items-center text-sm text-text-muted space-x-4">
                  <div className="flex items-center">
                    <Clock size={16} className="mr-2" />
                    {selectedBlog.readTime}
                  </div>
                  <span>•</span>
                  <span>{formatDate(selectedBlog.updated_at || selectedBlog.publishDate)}</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button 
                  onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                  className="btn-base btn-primary btn-sm"
                >
                  Share on LinkedIn
                </Button>
                <button 
                  onClick={handleCloseBlogModal}
                  className="text-text-secondary hover:text-text-primary p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-8 max-w-5xl mx-auto">
                {/* Article Title */}
                <h1 className="text-4xl md:text-6xl font-bold text-text-primary mb-8 font-grotesk leading-tight">
                  {selectedBlog.title}
                </h1>
                
                {/* Featured Image */}
                {(selectedBlog.image || '/api/placeholder/800/400') && (
                  <div className="mb-10">
                    <img 
                      src={selectedBlog.image || '/api/placeholder/800/400'} 
                      alt={selectedBlog.title}
                      className="w-full h-96 object-cover rounded-2xl shadow-lg"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/800/400';
                      }}
                    />
                  </div>
                )}
                
                {/* Article Excerpt */}
                <div className="mb-10">
                  <div 
                    className="text-xl text-text-secondary font-medium leading-relaxed rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedBlog.excerpt }}
                  />
                </div>
                
                {/* Article Content */}
                <div className="prose prose-xl max-w-none">
                  <div 
                    className="space-y-8 text-text-secondary leading-relaxed text-lg rich-text-content"
                    dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                  />
                </div>
                
                {/* Tags Section */}
                {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                  <div className="mt-16 pt-8 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-text-primary mb-4">Related Topics:</h4>
                    <div className="flex flex-wrap gap-3">
                      {selectedBlog.tags.map((tag, index) => (
                        <Badge key={index} className="bg-indigo-100 text-indigo-800 px-4 py-2 text-sm font-medium">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Call to Action */}
                <div className="mt-16 p-8 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl">
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Connect with Gideon</h3>
                  <p className="text-text-secondary mb-6">
                    Interested in discussing AI implementation, team leadership, or investment management? 
                    Let's connect and explore opportunities for collaboration.
                  </p>
                  <div className="flex gap-4">
                    <Button 
                      onClick={() => window.open('https://linkedin.com/in/gideonmetre', '_blank')}
                      className="btn-base btn-primary"
                    >
                      Connect on LinkedIn
                    </Button>
                    <Button 
                      onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                      className="btn-base btn-secondary"
                    >
                      Send Message
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BlogSection;